/**
 * Percentage duration discounts of a scooter listing — form helpers.
 *
 * Validation only: the SAME rules as the server
 * (backend/services/scooter_pricing.py validate_percentage_discounts), so the
 * form can explain a problem before saving. The server checks again, and every
 * price (examples, quotes, bookings) is computed by the server — never here.
 */
export const MAX_DISCOUNTS = 5;
export const MIN_THRESHOLD_DAYS = 2;
export const MAX_THRESHOLD_DAYS = 90;
export const MAX_DISCOUNT_PERCENT = 50;

export const PRICING_MODE_LEGACY = 'legacy';
export const PRICING_MODE_PERCENTAGE = 'percentage';

export const pricingModeOf = (service) =>
  service?.details?.scooter?.pricing_mode === PRICING_MODE_PERCENTAGE ? PRICING_MODE_PERCENTAGE : PRICING_MODE_LEGACY;

// Which discounts a percentage listing uses (server: details.scooter.discount_source).
export const SOURCE_GLOBAL = 'global';
export const SOURCE_CUSTOM = 'custom';
export const SOURCE_NONE = 'none';
export const DISCOUNT_SOURCES = [SOURCE_GLOBAL, SOURCE_CUSTOM, SOURCE_NONE];

/** null for a legacy listing; a percentage listing saved without a source uses its own discounts. */
export const discountSourceOf = (service) => {
  if (pricingModeOf(service) !== PRICING_MODE_PERCENTAGE) return null;
  const source = service?.details?.scooter?.discount_source;
  return DISCOUNT_SOURCES.includes(source) ? source : SOURCE_CUSTOM;
};

/** Saved discounts → editable rows (strings, as typed in inputs). */
export const toRows = (discounts) =>
  (discounts || []).map((d) => ({ min_days: String(d.min_days ?? ''), discount_percentage: String(d.discount_percentage ?? '') }));

const normalized = (raw) => String(raw ?? '').trim().replace(',', '.');
const asNumber = (raw) => {
  const s = normalized(raw);
  if (s === '' || !/^-?\d+(\.\d+)?$/.test(s)) return null;
  return Number(s);
};
// At most 2 decimals, trailing zeros ignored ("12.50" ok, "12.505" not) — decimal, not float, like the server.
const hasAtMostTwoDecimals = (raw) => (normalized(raw).split('.')[1] || '').replace(/0+$/, '').length <= 2;

/**
 * → { ok, discounts (sorted, ready to send), errors: [{ row (index or null), code, params }] }.
 * Codes are i18n keys under scooterPricing.errors.
 */
export function validateDiscountRows(rows) {
  const errors = [];
  const parsed = [];
  if ((rows || []).length > MAX_DISCOUNTS) errors.push({ row: null, code: 'tooMany', params: { max: MAX_DISCOUNTS } });
  (rows || []).forEach((r, i) => {
    const days = asNumber(r.min_days);
    const pct = asNumber(r.discount_percentage);
    if (days === null) errors.push({ row: i, code: 'daysRequired' });
    else if (!Number.isInteger(days)) errors.push({ row: i, code: 'daysInteger' });
    else if (days < MIN_THRESHOLD_DAYS || days > MAX_THRESHOLD_DAYS) {
      errors.push({ row: i, code: 'daysRange', params: { min: MIN_THRESHOLD_DAYS, max: MAX_THRESHOLD_DAYS } });
    }
    if (pct === null) errors.push({ row: i, code: 'pctRequired' });
    else if (pct < 0 || pct > MAX_DISCOUNT_PERCENT) errors.push({ row: i, code: 'pctRange', params: { max: MAX_DISCOUNT_PERCENT } });
    else if (!hasAtMostTwoDecimals(r.discount_percentage)) errors.push({ row: i, code: 'pctDecimals' });
    parsed.push({ row: i, min_days: days, discount_percentage: pct });
  });
  if (!errors.length) {
    const sorted = [...parsed].sort((a, b) => a.min_days - b.min_days);
    for (let k = 1; k < sorted.length; k += 1) {
      const a = sorted[k - 1];
      const b = sorted[k];
      if (a.min_days === b.min_days) errors.push({ row: b.row, code: 'duplicate', params: { days: b.min_days } });
      else if (b.discount_percentage < a.discount_percentage) {
        errors.push({ row: b.row, code: 'decreasing', params: { days: b.min_days, prevDays: a.min_days, prevPct: a.discount_percentage } });
      }
    }
    if (!errors.length) {
      return { ok: true, errors, discounts: sorted.map(({ min_days, discount_percentage }) => ({ min_days, discount_percentage })) };
    }
  }
  return { ok: false, errors, discounts: null };
}

/** "10", "12.5" — percentages as people write them (locale decimal separator). */
export const formatPct = (value, locale) =>
  Number(value).toLocaleString(locale, { maximumFractionDigits: 2 });
